import styles from "./MasterUI.module.css";

import { SettingsWorkBox } from "../workBoxes/SettingsWorkBox";
import React, { useCallback, useRef, useState } from "react";
import { MasterTopBar } from "./MasterTopBar";
import { useOuterClickManyTargets } from "@kalyzee/kast-web-sdk/web-components";
import { OverlayContainer, ReactIcons, RevealContainer } from "@kalyzee/kalyzee-ui";
import { useAppStoreSelector } from "../../contexts/appStore/selectors";
import { useMeetingSelector } from "@kalyzee/kast-web-sdk";
import { useMeetingContainerContext } from "../../contexts/meetingContainer/context";
import { isExpert } from "../../helpers/roles";

export interface MasterUIProps {
  children: React.ReactNode;
  onBack?: () => void;
}

export const MasterUI = React.memo(({ children, onBack }: MasterUIProps) => {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [meetingInfoOpen, setMeetingInfoOpen] = useState(false);
  const isMobile = useAppStoreSelector(state => state.isMobile);

  const settingsBtnRef: React.RefObject<HTMLDivElement> = useRef(null);
  const settingsBoxRef: React.RefObject<HTMLDivElement> = useRef(null);
  const meetingTimerRef: React.RefObject<HTMLDivElement> = useRef(null);
  const meetingInfoRef: React.RefObject<HTMLDivElement> = useRef(null);

  // Meeting data for mobile info popup
  const containerCtx = useMeetingContainerContext({ raiseIfUndefined: false });
  const [meetingName] = useMeetingSelector(containerCtx?.containerAPI, "meeting", meeting => meeting?.name);
  const [watchingNumber] = useMeetingSelector(containerCtx?.containerAPI, "sessions", sessions => sessions?.filter(s => !isExpert(s)).length);

  useOuterClickManyTargets([settingsBtnRef, settingsBoxRef], () => {
    setSettingsOpen(false);
  });

  useOuterClickManyTargets([meetingTimerRef, meetingInfoRef], () => {
    setMeetingInfoOpen(false);
  });

  const onSettings = useCallback(() => {
    setSettingsOpen(v => !v);
  }, []);

  const onMeetingTimerClick = useCallback(() => {
    setMeetingInfoOpen(v => !v);
  }, []);

  const renderSettingsOverlayWorkBox = () => {
    if (isMobile) {
      return (
        <OverlayContainer
          visible={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          style={{ position: "absolute", inset: 0, zIndex: 10 }}
          overlay={
            <div
              ref={settingsBoxRef}
              className={styles.mobileSettingsOverlay}
              onClick={(e) => e.stopPropagation()}
            >
              <SettingsWorkBox containerRef={settingsBoxRef} onClose={() => setSettingsOpen(false)} />
            </div>
          }
        />
      );
    }

    return (
      <RevealContainer
        open={settingsOpen}
        duration={300}
        easing="ease-in-out"
        className={styles.settingsBox}
      >
        <SettingsWorkBox
          containerRef={settingsBoxRef}
          onClose={() => setSettingsOpen(false)}
        />
      </RevealContainer>
    );
  };
  
  const renderMeetingInfoPopup = () => {
    if (!isMobile || !meetingInfoOpen) return null;
    if (!meetingName && watchingNumber === undefined) return null;
    return (
      <div ref={meetingInfoRef} className={styles.meetingInfoPopup}>
        {meetingName && (
          <div className={styles.meetingInfoRow}>
            <span className={styles.meetingInfoLabel}>Cours</span>
            <span className={styles.meetingInfoValue}>{meetingName}</span>
          </div>
        )}
        {watchingNumber !== undefined && (
          <div className={styles.meetingInfoRow}>
            <span className={styles.meetingInfoLabel}>Participants</span>
            <span className={styles.meetingInfoValue}>
              <ReactIcons.Eye style={{ width: "1em", height: "1em" }} />
              {watchingNumber}
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div style={{ position: "relative" }}>
        <MasterTopBar
          onSettings={onSettings}
          onBack={onBack}
          settingsBtnRef={settingsBtnRef}
          meetingTimerRef={meetingTimerRef}
          onMeetingTimerClick={isMobile ? onMeetingTimerClick : undefined}
        />
        {renderMeetingInfoPopup()}
      </div>
      <div className={styles.content}>
        {children}
        {renderSettingsOverlayWorkBox()}
      </div>
    </div>
  );
});